/*
 * Copyright (c) 2020 SQUAREPING INC.
 *
 * SQUAREPING CONFIDENTIAL
 * [2019 and Beyond] SQUAREPING INC. All Rights Reserved.
 *
 * NOTICE: The source code contained herein is, and remains the property of SQUAREPING INC.
 * and its suppliers, if any. The intellectual and technical concepts contained herein are
 * proprietary to SQUAREPING INC. and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 *
 * Dissemination of this information or reproduction of this material is strictly forbidden
 * anywhere in the world unless prior written permission is obtained from SQUAREPING INC.
 */

import i18next, { i18n } from 'i18next';
import moment from 'moment';
import { initReactI18next } from 'react-i18next';
import { DeferredPromise } from '../utils/promise';

const getResources = (language = 'en') => {
  switch (language) {
    case 'vi':
      return require('./vi').default;
    default:
      return require('./en').default;
  }
};

export class LanguageModule {
  static languageChangedListeners: Array<() => void> = [];
  static initTask = new DeferredPromise<i18n>();
  static async initLanguage(language = 'en'): Promise<i18n> {
    await i18next
      .use(initReactI18next) // passes i18n down to react-i18next
      .init({
        lng: language,
        fallbackLng: 'en',
        interpolation: {
          escapeValue: false, // not needed for react as it does escape per default to prevent xss!
        },
        debug: false,
        initImmediate: false,
        resources: {
          [language]: getResources(language),
        },
      });

    require('./formatter'); // only run this after init

    if (language !== 'en') await require('moment/locale/' + language.toLowerCase());
    moment.locale(language.toLowerCase());

    LanguageModule.notifyListeners();

    return i18next;
  }

  static addOnLanguageChangedListener(task: () => void) {
    LanguageModule.languageChangedListeners.push(task);
  }

  static notifyListeners() {
    LanguageModule.languageChangedListeners.forEach((t) => t());
  }

  static async changeLanguage(language = 'en'): Promise<void> {
    if (language !== 'en') await require('moment/locale/' + language.toLowerCase());

    // update moment
    moment.locale(language.toLowerCase());

    // add new language resource
    const localeRes = getResources(language);
    Object.entries(localeRes).forEach(([ns, data]) => {
      i18next.addResourceBundle(language, ns, data, true, true);
    });

    // change language
    await i18next.changeLanguage(language, (res) => {
      //console.log('Change language calback', res);
    });
    LanguageModule.notifyListeners();
  }
}
